import React, { createRef, useCallback, useEffect, useMemo, useState } from 'react';
import { IChartProps } from 'bizcharts/lib/interface';
import { ILegend } from 'bizcharts/lib/components/Legend';
import { useSelector } from 'react-redux';
import { useRequest } from 'ahooks';
import type { BrandSocialDataType } from '@/services/swagger/data-contracts';
import LineAndBarMixedChart from '@/components/charts/LineAndBarMixedChart';
import { RootState } from '@/models';
import { ConBrandWebController } from '@/services/swagger/ConBrandWebController';
import TooltipBase from '@/components/tooltip';
import ChartHeader from '../chart-header';
import Chart from '../chart';
import TabLabel from '../TabLabel';
import LockedCard, { LockCardState } from '../LockedCard';
import styles from './index.module.scss';

const controller = new ConBrandWebController();

export type MarkertingAbilityProps = {
  /** 品牌id */
  conBrandId: string;
  /** 品牌解锁状态 */
  brandLockState?: string;
  /** 品牌官号互动量：转发指数 */
  repostList?: BrandSocialDataType[];
  /** 品牌官号互动量：点赞指数 */
  likeList?: BrandSocialDataType[];
  /** 品牌官号互动量：评论指数 */
  commentList?: BrandSocialDataType[];
  /** 品牌解锁状态改变回调 */
  onLockedCardStateChange?: (state: string) => void;
};

const chartPanelPadding = 24;
const tabLabelMargin = 43.5;
const markLineWidth = 72;

const scale: IChartProps['scale'] = {
  countTimeStr: {
    type: 'cat',
    alias: '月份',
    formatter(value) {
      return `${value}月`;
    },
  },
  growRate: {
    type: 'linear',
    min: 0,
    max: 100,
    tickCount: 2,
    nice: true,
    alias: '增长率',
    formatter(value) {
      return `${value}%`;
    },
  },
  value: {
    type: 'linear',
    //   tickCount: 2,
    nice: true,
    alias: '数量',
  },
};

const legendProps: ILegend = {
  name: 'dataTypeName',
  padding: [14, 0, 14, 0],
  // marker: {
  //   symbol: (x, y) => {
  //     const r = 2;
  //     return [
  //       ['M', x - 4 * r, y],
  //       ['L', x + 4 * r, y],
  //       ['M', x - r, y],
  //       ['A', r, r, 0, 0, 0, x + r, y],
  //       ['A', r, r, 0, 0, 0, x - r, y],
  //     ];
  //   },
  //   style(style) {
  //     const { stroke } = style;
  //     return { ...style, fill: stroke };
  //   },
  // },
  itemName: {
    style: {
      fill: '#999',
    },
  },
};
const colors = ['#f75c2f', '#ffc2b0', '#666666', '#b8b0ad'];
const MarkertingAbility: React.FC<MarkertingAbilityProps> = ({
  conBrandId,
  repostList = [],
  likeList = [],
  commentList = [],
  brandLockState = 'not_member',
  onLockedCardStateChange,
}) => {
  const { token } = useSelector((state: RootState) => state.user);
  /** 品牌评分信息 */
  const { data: marketing } = useRequest(
    async () => {
      const res = await controller.conBrandWebControllerV1MarketingCapacityUsingGet1({ conBrandId });
      return res.data.data;
    },
    {
      refreshDeps: [brandLockState],
    }
  );
  //暂时隐藏微博数据
  if (marketing?.relatedContentList) {
    marketing.relatedContentList = marketing?.relatedContentList?.filter(item => item.dataTypeName !== '微博');
  }

  const tabLabelListWithRef = useMemo(
    () => [
      { ref: createRef<HTMLSpanElement>(), name: '转发指数', value: 'repost', data: repostList },
      { ref: createRef<HTMLSpanElement>(), name: '点赞指数', value: 'like', data: likeList },
      { ref: createRef<HTMLSpanElement>(), name: '评论指数', value: 'comment', data: commentList },
    ],
    [commentList, likeList, repostList]
  );
  const [selectedLabel, setSelectedLabel] = useState(tabLabelListWithRef[0]);
  const [selectedLabelIndex, setSelectedLabelIndex] = useState(0);
  const [markLineStyle, setMarkeLineStyle] = useState({});
  const onStateChange = useCallback(
    (state: string) => {
      onLockedCardStateChange?.(state);
    },
    [onLockedCardStateChange]
  );
  const calculateMarkLineStyle = useCallback(
    (index: number) => {
      const left =
        chartPanelPadding +
        tabLabelListWithRef.slice(0, index).reduce((acc, cur) => {
          return acc + cur.ref.current.offsetWidth;
        }, 0) +
        tabLabelMargin * index +
        ((tabLabelListWithRef[index].ref.current?.offsetWidth || 0) - markLineWidth) / 2;
      setMarkeLineStyle({ left });
    },
    [tabLabelListWithRef]
  );
  useEffect(() => {
    calculateMarkLineStyle(selectedLabelIndex);
  }, [calculateMarkLineStyle, selectedLabelIndex]);
  return (
    !!token && (
      <div className={styles.marketingAbility}>
        <ChartHeader
          title={
            <h2 className={styles.moduleTitle}>
              营销能力
              <TooltipBase title="由品牌内容量、互动量等维度搭建的数学模型计算得出。" placement="right" />
            </h2>
          }
          titleFontSize={18}
          className={styles.marketingChartHeader}
        />
        <Chart
          title={
            <h2 className={styles.chartTitle}>
              品牌官号内容互动指数
              <TooltipBase
                title={
                  <>
                    <p>品牌多平台官方账号内容的互动总数，数据已脱敏处理。</p>
                    <p>数据来源：CBNData数据库</p>
                  </>
                }
                placement="right"
              />
            </h2>
          }
          panelClassName={styles.officialAccountChart}
        >
          <div className={styles.tabLabelList}>
            {tabLabelListWithRef.map((item, index) => {
              return (
                <TabLabel
                  ref={item.ref}
                  key={item.value}
                  className={styles.tabLabel}
                  name={item.name}
                  selected={item.value === selectedLabel.value}
                  onClick={() => {
                    setSelectedLabel(item);
                    setSelectedLabelIndex(index);
                    calculateMarkLineStyle(index);
                  }}
                />
              );
            })}
          </div>
          <div className={styles.splitLine} />
          <div className={styles.markLine} style={markLineStyle} />
          <LineAndBarMixedChart
            // lineChartData={tabLabelListWithRef[selectedLabelIndex].data}
            barChartData={tabLabelListWithRef[selectedLabelIndex].data}
            xAxisName="countTimeStr"
            // yAxisNameOfLineChart="value"
            yAxisNameOfBarChart="value"
            // percentKeyName="growRate"
            scale={scale}
            // lineChartGroupBy="dataTypeName"
            barChartGroupBy="dataTypeName"
            // lineColor={colors}
            intervalColor={colors}
            legendProps={legendProps}
            pointProps={{ size: 3 }}
          />
        </Chart>
        <Chart
          title={
            <h2 className={styles.chartTitle}>
              品牌官号内容量指数
              <TooltipBase
                title={
                  <>
                    <p>品牌多平台官方账号发布的内容总数，数据已脱敏处理。</p>
                    <p>数据来源：CBNData数据库</p>
                  </>
                }
                placement="right"
              />
            </h2>
          }
          panelClassName={styles.generatedContentChart}
        >
          {brandLockState !== 'unlocked' ? (
            <LockedCard
              conBrandId={conBrandId}
              backgroundImgUrl="//assets-oss.cbndata.com/cbndata-refactor-fe/FlCaIEGkUG6pzWVMUHXItHMfwam5.png"
              explanation="此处为品牌内容渠道营销费用占比、内容渠道营销ROI等方面
              若想查看该信息，请先开通中国新消费品牌数据库会员"
              state={brandLockState as LockCardState}
              className={styles.generatedContentLockedCard}
              explanationClassName={styles.generatedContentLockedExplanation}
              onStateChange={onStateChange}
            />
          ) : (
            <LineAndBarMixedChart
              // lineChartData={marketing?.productionList}
              barChartData={marketing?.productionList}
              xAxisName="countTimeStr"
              // yAxisNameOfLineChart="value"
              yAxisNameOfBarChart="value"
              // percentKeyName="growRate"
              scale={scale}
              // lineChartGroupBy="dataTypeName"
              barChartGroupBy="dataTypeName"
              // lineColor={colors}
              intervalColor={colors}
              legendProps={legendProps}
              pointProps={{ size: 3 }}
            />
          )}
        </Chart>
        <Chart
          title={
            <h2 className={styles.chartTitle}>
              平台相关内容量指数
              <TooltipBase
                title={
                  <>
                    <p>社交平台中与品牌相关的所有内容总数，数据已脱敏处理。</p>
                    <p>数据来源：西瓜微数、千瓜数据</p>
                  </>
                }
                placement="right"
              />
            </h2>
          }
          panelClassName={styles.platformChart}
        >
          {brandLockState !== 'unlocked' ? (
            <LockedCard
              conBrandId={conBrandId}
              backgroundImgUrl="//assets-oss.cbndata.com/cbndata-refactor-fe/FlCaIEGkUG6pzWVMUHXItHMfwam5.png"
              explanation={
                <>
                  <span>此处为各平台与品牌相关内容量数据</span>
                  <span>若想查看该信息，请先开通中国新消费品牌数据库会员</span>
                </>
              }
              state={brandLockState as LockCardState}
              className={styles.platformLockedCard}
              explanationClassName={styles.platformLockedExplanation}
              onStateChange={onStateChange}
            />
          ) : (
            <LineAndBarMixedChart
              // lineChartData={marketing?.relatedContentList}
              barChartData={marketing?.relatedContentList}
              xAxisName="countTimeStr"
              // yAxisNameOfLineChart="value"
              yAxisNameOfBarChart="value"
              // percentKeyName="growRate"
              scale={scale}
              // lineChartGroupBy="dataTypeName"
              barChartGroupBy="dataTypeName"
              // lineColor={colors}
              intervalColor={colors}
              legendProps={legendProps}
              pointProps={{ size: 3 }}
            />
          )}
        </Chart>
      </div>
    )
  );
};

export default MarkertingAbility;
